import {useLocaleKeys} from '../../../LocaleKeysProvider';
import {LocaleKeys} from '../../../../../locale-keys/LocaleKeys';
import {Maybe, Price} from '@wix/wixstores-graphql-schema-node';
import {useControllerProps} from '../../ControllerContext';
import {useCurrencyFormatter} from '../../../../../hooks/useCurrencyFormatter';

type TaxLine = {title: string; value: string};

export function useTaxBreakdownLines() {
  const localeKeys = useLocaleKeys();
  const {taxInfo, taxName} = useControllerProps().thankYouPageStore;
  const format = useCurrencyFormatter();

  let lines: TaxLine[] = [];

  if (taxInfo?.taxBreakdown?.some((item) => item?.taxName)) {
    lines = taxInfo.taxBreakdown.reduce<TaxLine[]>((result, item) => {
      /* istanbul ignore else */
      if (item.aggregatedTaxAmount?.amount) {
        return result.concat(
          buildTaxLines({
            taxName: item.taxName,
            taxAmount: item.aggregatedTaxAmount,
            exemptAmount: item.aggregatedExemptAmount,
            localeKeys,
            format,
          })
        );
      }
      /* istanbul ignore next */
      return result;
    }, []);
  } else if (taxInfo?.totalTax?.amount) {
    lines = buildTaxLines({
      taxName,
      taxAmount: taxInfo.totalTax,
      exemptAmount: taxInfo.totalExempt,
      localeKeys,
      format,
    });
  }

  return lines;
}

function buildTaxLines({
  taxAmount,
  exemptAmount,
  taxName,
  localeKeys,
  format,
}: {
  taxAmount: Maybe<Price>;
  exemptAmount: Maybe<Price>;
  taxName?: Maybe<string>;
  localeKeys: LocaleKeys;
  format: (value: string | number) => string;
}): TaxLine[] {
  const exemptAmountValue = parseFloat(exemptAmount?.amount ?? /* istanbul ignore next */ '0');
  const taxAmountValue = parseFloat(taxAmount?.amount ?? /* istanbul ignore next */ '0');
  const hasExemption = exemptAmountValue > 0;
  const isPartialExemption = taxAmountValue > 0;

  const title: string = taxName ?? localeKeys.thankYouPage.tax.label();

  if (!hasExemption) {
    return [{title, value: format(taxAmountValue)}];
  }

  if (!isPartialExemption) {
    return [
      {
        title: localeKeys.thankYouPage.taxExemption.full.label({name: title}) as string,
        value: format(taxAmountValue),
      },
    ];
  }

  return [
    {title, value: format(taxAmountValue + exemptAmountValue) ?? /* istanbul ignore next */ ''},
    {
      title: localeKeys.thankYouPage.taxExemption.partial.label(),
      value: `-${format(exemptAmountValue)}`,
    },
  ];
}
